import { mergeSessions } from "./mergeSessions";
import {
  AnyWizardDefinition,
  WizardSession,
  WizardSessionsSnapshot,
} from "./types";

const STORAGE_KEY_PREFIX = "fp_wizard_sessions:";

const DAY_MS = 24 * 60 * 60 * 1000;
export const IN_PROGRESS_SESSION_TTL_MS = 30 * DAY_MS;
export const COMPLETED_SESSION_TTL_MS = DAY_MS;

export type WizardStorageScope = { userID: string; envID: string };

type StoredSession = Omit<WizardSession, "id" | "updatedAt"> & {
  updatedAt?: number;
  version?: number;
};

const emptySnapshot = (): WizardSessionsSnapshot => ({
  sessions: {},
  discarded: {},
});

export function wizardStorageKey({ userID, envID }: WizardStorageScope) {
  return `${STORAGE_KEY_PREFIX}${userID}:${envID}`;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === "object" && !Array.isArray(value);
}

function isStoredSession(value: unknown): value is StoredSession {
  if (!isRecord(value)) {
    return false;
  }
  return (
    typeof value.type === "string" &&
    typeof value.currentStepId === "string" &&
    Array.isArray(value.completedStepIds) &&
    isRecord(value.data) &&
    typeof value.startedAt === "number"
  );
}

function isExpired(session: WizardSession, now: number): boolean {
  const ttl =
    session.completedAt === null
      ? IN_PROGRESS_SESSION_TTL_MS
      : COMPLETED_SESSION_TTL_MS;
  return now - session.updatedAt > ttl;
}

/**
 * Rebuilds a stored session against the current definition, or returns
 * undefined when it can't be trusted (unregistered type, unmigratable
 * version). A `currentStepId` that no longer exists is kept; the panel falls
 * forward to an applicable step.
 */
function restoreSession(
  id: string,
  stored: StoredSession,
  definitions: Record<string, AnyWizardDefinition>,
): WizardSession | undefined {
  const definition = definitions[stored.type];
  if (!definition) {
    return undefined;
  }

  try {
    const storedVersion = stored.version ?? 1;
    const data =
      storedVersion === (definition.version ?? 1)
        ? stored.data
        : definition.migrate?.(stored.data, storedVersion);
    if (!data) {
      return undefined;
    }
    const stepIds = new Set(definition.steps.map((step) => step.id));
    return {
      id,
      type: stored.type,
      currentStepId: stored.currentStepId,
      completedStepIds: stored.completedStepIds.filter((stepId) =>
        stepIds.has(stepId),
      ),
      ...(Array.isArray(stored.applicableStepIds)
        ? {
            applicableStepIds: stored.applicableStepIds.filter((stepId) =>
              stepIds.has(stepId),
            ),
          }
        : {}),
      data: { ...definition.createInitialData(stored.params), ...data },
      params: stored.params,
      startedAt: stored.startedAt,
      updatedAt: stored.updatedAt ?? stored.startedAt,
      completedAt: stored.completedAt ?? null,
    };
  } catch {
    return undefined;
  }
}

/**
 * Parses a stored value, dropping anything that no longer matches a
 * registered wizard, expired sessions (idle in-progress sessions after 30
 * days, completed ones after a day) and old discard records.
 */
export function parseWizardSessions(
  raw: string | null,
  definitions: Record<string, AnyWizardDefinition>,
  now: number,
): WizardSessionsSnapshot {
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw ?? "{}");
  } catch {
    return emptySnapshot();
  }
  if (!isRecord(parsed)) {
    return emptySnapshot();
  }

  const snapshot = emptySnapshot();
  if (isRecord(parsed.discarded)) {
    for (const [id, discardedAt] of Object.entries(parsed.discarded)) {
      if (
        typeof discardedAt === "number" &&
        now - discardedAt <= IN_PROGRESS_SESSION_TTL_MS
      ) {
        snapshot.discarded[id] = discardedAt;
      }
    }
  }
  if (isRecord(parsed.sessions)) {
    for (const [id, value] of Object.entries(parsed.sessions)) {
      const session = isStoredSession(value)
        ? restoreSession(id, value, definitions)
        : undefined;
      if (session && !isExpired(session, now)) {
        snapshot.sessions[id] = session;
      }
    }
  }
  return mergeSessions(snapshot, emptySnapshot());
}

export function loadWizardSessions(
  storage: Storage,
  scope: WizardStorageScope,
  definitions: Record<string, AnyWizardDefinition>,
  now = Date.now(),
): WizardSessionsSnapshot {
  let raw: string | null;
  try {
    raw = storage.getItem(wizardStorageKey(scope));
  } catch {
    return emptySnapshot();
  }
  return parseWizardSessions(raw, definitions, now);
}

/**
 * Merges `snapshot` into what is already stored rather than overwriting it,
 * so sessions another tab saved since this tab last read survive.
 */
export function saveWizardSessions(
  storage: Storage,
  scope: WizardStorageScope,
  snapshot: WizardSessionsSnapshot,
  definitions: Record<string, AnyWizardDefinition>,
  now = Date.now(),
): void {
  const key = wizardStorageKey(scope);
  try {
    const existing = storage.getItem(key);
    const merged = mergeSessions(
      snapshot,
      parseWizardSessions(existing, definitions, now),
    );
    if (
      Object.keys(merged.sessions).length === 0 &&
      Object.keys(merged.discarded).length === 0
    ) {
      if (existing !== null) {
        storage.removeItem(key);
      }
      return;
    }

    const sessions = Object.fromEntries(
      Object.entries(merged.sessions).map(([id, session]) => {
        const definition = definitions[session.type];
        const sanitize = definition?.sanitizeForStorage;
        return [
          id,
          {
            ...session,
            data: sanitize ? sanitize(session.data) : session.data,
            version: definition?.version ?? 1,
          },
        ];
      }),
    );
    const serialized = JSON.stringify({
      sessions,
      discarded: merged.discarded,
    });
    if (serialized !== existing) {
      storage.setItem(key, serialized);
    }
  } catch (error) {
    // Quota or privacy-mode failures only cost cross-reload persistence; the
    // in-memory session keeps working.
    console.warn("Unable to persist wizard sessions", error);
  }
}

/** Removes every user's and environment's saved sessions from this browser. */
export function clearWizardSessions(storage: Storage = window.localStorage) {
  try {
    const keys: string[] = [];
    for (let index = 0; index < storage.length; index++) {
      const key = storage.key(index);
      if (key?.startsWith(STORAGE_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => storage.removeItem(key));
  } catch (error) {
    console.warn("Unable to clear wizard sessions", error);
  }
}
